import { AccountMenu as CosmicAccountMenu, AccountMenuClickableProps } from '@taxbit-private/cosmic'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { useAuth } from 'contexts/auth/AuthContext'
import { useOrg } from 'contexts/OrgContext'
import { useUser } from 'contexts/UserContext'
import { useDisplayOrg } from 'hooks/utils/useDisplayOrgName'
import { useDynamicLabels } from 'layouts/app/useDynamicLabels'
import { isZeusEnvironment } from 'utils/environment'

import AccountMenuNestedOrgView from './AccountMenuNestedView'
import CreateOrgModal from './CreateOrgModal'
import { useOrgLegalEntities } from './hooks/useOrgLegalEntities'

enum AccountMenuTrackingId {
  OrganizationMenu = 'account-menu-organization-menu',
  SwitchOrganization = 'account-menu-switch-organization',
  CreateNewOrganization = 'account-menu-create-new-organization',
  HelpCenter = 'account-menu-help-center',
}

const AccountMenu = () => {
  const { user } = useUser()
  const { currentUser, authManager } = useAuth()
  const { org } = useOrg()
  const { findParentOrg, isLoading, hasEntities } = useOrgLegalEntities()
  const [isCreateOrgModalOpen, setIsCreateOrgModalOpen] = useState(false)
  const { allLegalEntitiesLabel } = useDynamicLabels()

  const [orgId, setOrgId] = useState(org)
  const [legalEntityId, setLegalEntityId] = useState<string | undefined>(undefined)
  useEffect(() => {
    if (!isLoading && org) {
      const parentOrgId = findParentOrg(org)

      if (parentOrgId) {
        // Current org is a legal entity, set its parent as orgId and itself as legalEntityId
        setOrgId(parentOrgId)
        setLegalEntityId(org)
      } else {
        // Current org is a parent org
        const hasLegalEntities = hasEntities(org)
        setOrgId(org)
        setLegalEntityId(hasLegalEntities ? allLegalEntitiesLabel : undefined)
      }
    }
  }, [
    findParentOrg,
    isLoading,
    org,
    setOrgId,
    setLegalEntityId,
    hasEntities,
    allLegalEntitiesLabel,
  ])

  const displayOrgName = useDisplayOrg(orgId)
  const displayLegalEntityNameFromOrg = useDisplayOrg(legalEntityId)

  // If legalEntityId is the allLegalEntitiesLabel, use it directly as the display name
  // since it's already a display string, not an org name that needs lookup
  const displayLegalEntityName =
    legalEntityId === allLegalEntitiesLabel ? allLegalEntitiesLabel : displayLegalEntityNameFromOrg

  const getNestedView = useCallback(() => <AccountMenuNestedOrgView />, [])

  const items = useMemo<AccountMenuClickableProps[]>(() => {
    const baseItems: AccountMenuClickableProps[] = [
      {
        label: 'Switch organization',
        iconName: 'shuffle',
        getNestedView,
        trackingId: AccountMenuTrackingId.SwitchOrganization,
      },
    ]

    if (!isZeusEnvironment()) {
      baseItems.push({
        label: 'Help center',
        iconName: 'search',
        trackingId: AccountMenuTrackingId.HelpCenter,
        href: 'https://info.taxbit.com/accounting',
        target: '_blank',
      })
    }

    if (user?.isSuperAdmin) {
      baseItems.push({
        label: 'Create new organization',
        iconName: 'plus',
        trackingId: AccountMenuTrackingId.CreateNewOrganization,
        onClick: () => {
          setIsCreateOrgModalOpen(true)
        },
      })
    }

    return baseItems
  }, [getNestedView, user?.isSuperAdmin])

  return (
    <>
      <CosmicAccountMenu
        trackingId={AccountMenuTrackingId.OrganizationMenu}
        title={displayOrgName ?? ' '}
        subtitle={displayLegalEntityName ?? ' '}
        userName={user?.user_name ?? ''}
        userEmail={user?.email ?? ''}
        avatarSrc={currentUser?.photoURL ?? ''}
        items={items}
        onLogout={() => {
          void authManager?.signOut()
        }}
      />
      <CreateOrgModal
        isOpen={isCreateOrgModalOpen}
        onClose={() => setIsCreateOrgModalOpen(false)}
      />
    </>
  )
}

export default AccountMenu
